import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  Stack,
  Link,
  useMediaQuery,
  useTheme,
  Avatar,
} from '@mui/material';
import moment from 'moment';
import { offsetToTimeString } from '@pv/common/utils';
import { marketplaceHost } from '@pv/common/api';
import { InquiryStatusChip } from './InquiryStatusChip';
import { useAuth } from '../../contexts/AuthContext';
import { EditEventPlanModal } from './EditEventPlanModal';
import { EventPlanInvitationModal } from './EventPlanInvitationModal';
import { useMutation } from '@apollo/client';
import { UPDATE_EVENT_PLAN } from './graphql';
import { UserAvatar } from '../UserAvatar';
import { EventPlanContext, useEventPlan, EventPlanType } from './useEventPlan';
const pvEnv = process.env.REACT_APP_PV_ENV as string;

const FindMoreEventSpacesButton = ({
  location,
}: {
  location: string | null;
}) => {
  let url: string;

  if (location) {
    const formattedLocation = location.replace(',', '').replace(' ', '-');
    url = `${marketplaceHost(pvEnv)}/cities/${formattedLocation}`;
  } else {
    url = `${marketplaceHost(pvEnv)}`;
  }

  return (
    <Button
      size="small"
      variant="outlined"
      color="secondary"
      href={url}
      target="_blank"
    >
      Find more event spaces
    </Button>
  );
};

export const EventPlan = ({
  eventPlan,
  cta = true,
}: {
  eventPlan: EventPlanType;
  cta?: boolean;
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false);
  const [updateEventPlan] = useMutation(UPDATE_EVENT_PLAN);

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleInviteClick = () => {
    setIsInvitationModalOpen(true);
  };

  return (
    <EventPlanContext.Provider value={{ eventPlan }}>
      <StyledEventPlanContainer>
        <EventPlanHeader
          onEditClick={handleEditClick}
          onInviteClick={handleInviteClick}
          cta={cta}
        />
        <EventsTable />
      </StyledEventPlanContainer>
      <EditEventPlanModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={(eventPlanInput) => {
          updateEventPlan({ variables: { input: eventPlanInput } });
        }}
      />
      <EventPlanInvitationModal
        isOpen={isInvitationModalOpen}
        onClose={() => setIsInvitationModalOpen(false)}
      />
    </EventPlanContext.Provider>
  );
};

const StyledEventPlanContainer = styled(Box)({
  border: '1px solid rgba(0, 0, 0, 0.05)',
  borderRadius: '16px',
  boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
});

const EventPlanHeader = ({
  onEditClick,
  onInviteClick,
  cta = true,
}: {
  onEditClick: () => void;
  onInviteClick: () => void;
  cta?: boolean;
}) => {
  const { eventPlan } = useEventPlan();
  const { guest } = useAuth();
  const theme = useTheme();
  const smMedia = useMediaQuery(theme.breakpoints.down('md'));
  const isOwner = guest?.email === eventPlan.guest.email;

  return (
    <Stack
      direction={smMedia ? 'column' : 'row'}
      justifyContent="space-between"
      alignItems={smMedia ? 'flex-start' : 'center'}
      gap={smMedia ? '16px' : '0px'}
      padding="16px"
    >
      <Stack direction="column" gap={'4px'}>
        <Typography variant="h6" component="h2" color="text.primary">
          {eventPlan.name}
        </Typography>
        <Stack direction="row">
          <EventPlanDetails />
        </Stack>
      </Stack>
      <Stack direction="row" spacing={1}>
        {eventPlan.location && cta && (
          <FindMoreEventSpacesButton location={eventPlan.location} />
        )}
        <Button
          size="small"
          variant="outlined"
          color="primary"
          onClick={onInviteClick}
        >
          Invite
        </Button>
        {isOwner && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={onEditClick}
          >
            Edit
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

const StyledTableHeader = styled(TableHead)({
  borderTop: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0px -1px 0px 0px rgba(0, 0, 0, 0.10) inset',
  '& th': {
    color: '#8F8F90',
    fontWeight: 400,
    fontSize: '14px',
  },
});

const StyledTableRow = styled(TableRow)({
  boxShadow: '0px -1px 0px 0px rgba(0, 0, 0, 0.10) inset',
  borderTop: '1px solid rgba(0, 0, 0, 0.05)',

  '&:last-of-type': {
    boxShadow: 'none',
  },
  '&:last-of-type td': {
    borderBottom: 'none',
  },
});

const StyledTableCell = styled(TableCell)({});

const EventsTable = () => {
  const { eventPlan } = useEventPlan();
  const theme = useTheme();
  const smMedia = useMediaQuery(theme.breakpoints.down('md'));
  if (eventPlan.events.length === 0) {
    return null;
  }
  return (
    <TableContainer>
      <Table>
        <StyledTableHeader>
          <StyledTableRow>
            <StyledTableCell></StyledTableCell>
            <StyledTableCell>Venue</StyledTableCell>
            <StyledTableCell>Space</StyledTableCell>
            <StyledTableCell>Inquiry Submitted</StyledTableCell>
            <StyledTableCell
              align={smMedia ? 'right' : 'left'}
              sx={{ width: '200px' }}
            >
              Status
            </StyledTableCell>
          </StyledTableRow>
        </StyledTableHeader>
        <TableBody>
          {eventPlan.events.map((event) => (
            <StyledTableRow key={event.id}>
              <StyledTableCell>
                <UserAvatar fullName={event.planner.fullName} />
              </StyledTableCell>
              <StyledTableCell>
                <Typography variant="body1">
                  <Stack direction="row" alignItems="center" gap="8px">
                    <Avatar
                      src={event.venue.logo}
                      sx={{ objectFit: 'contain' }}
                    />
                    <Link
                      href={event.venue.pathname}
                      target="_blank"
                      underline="hover"
                      sx={{ color: 'text.primary' }}
                    >
                      {event.venue.name}
                    </Link>
                  </Stack>
                </Typography>
              </StyledTableCell>
              <StyledTableCell>
                {event.spaces.map((space) => {
                  return (
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap="8px"
                      key={space.id}
                    >
                      {space.images.length > 0 && (
                        <img
                          src={space.images[0].url}
                          alt={`${space.name}`}
                          style={{
                            width: '48px',
                            borderRadius: '8px',
                          }}
                        />
                      )}
                      <Typography variant="body1">
                        <Link
                          href={space.pathname}
                          key={space.id}
                          target="_blank"
                          underline="hover"
                          sx={{ color: 'text.primary' }}
                        >
                          {space.name}
                        </Link>
                      </Typography>
                    </Stack>
                  );
                })}
              </StyledTableCell>
              <StyledTableCell>
                <Typography variant="body1">
                  {formatInquirySentAt(event.createdAt)}
                </Typography>
              </StyledTableCell>
              <StyledTableCell
                sx={{ width: '200px' }}
                align={smMedia ? 'right' : 'left'}
              >
                {event.inquiryStatus === 'pending' ? (
                  <Link
                    href={returnToBookingUrl(
                      event.venue.id,
                      event.spaces[0].id,
                      event.slug,
                    )}
                    target="_blank"
                  >
                    <InquiryStatusChip
                      hasResponse={!!event.venueRespondedAt}
                      status={event.inquiryStatus}
                    />
                  </Link>
                ) : (
                  <InquiryStatusChip
                    hasResponse={!!event.venueRespondedAt}
                    status={event.inquiryStatus}
                  />
                )}
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const EventPlanDetails = () => {
  const { eventPlan } = useEventPlan();
  const formattedDate =
    eventPlan.startDate && moment(eventPlan.startDate).format('MMM D, YYYY');

  let formattedTime;

  if (eventPlan.startOffset && eventPlan.endOffset) {
    const formattedStartTime = offsetToTimeString(eventPlan.startOffset);
    const formattedEndTime = offsetToTimeString(eventPlan.endOffset);
    formattedTime = [formattedStartTime, formattedEndTime].join(' - ');
  }

  const formattedGroupSize =
    eventPlan.groupSize && `${eventPlan.groupSize} people`;
  const details = [formattedDate, formattedTime, formattedGroupSize].filter(
    Boolean,
  );
  return (
    <Stack direction="row" gap={'4px'}>
      {details.filter(Boolean).map((detail, index) => (
        <React.Fragment key={detail}>
          <Typography key={detail} variant="body2" color="text.secondary">
            {detail}
          </Typography>
          {index < details.length - 1 && (
            <Box sx={{ color: 'text.secondary', fontSize: '12px' }}>•</Box>
          )}
        </React.Fragment>
      ))}
    </Stack>
  );
};

const returnToBookingUrl = (venueId: string, spaceId: string, slug: string) => {
  return `/venues/${venueId}/spaces/${spaceId}/bookings/${slug}`;
};

const formatInquirySentAt = (inquirySentAt: Date) => {
  const dateString = moment(inquirySentAt).format('MMM D, YYYY');
  const daysAgo = moment().diff(moment(inquirySentAt), 'days');

  if (daysAgo === 0) {
    return 'Today';
  }

  return `${dateString} (${daysAgo} days ago)`;
};
